import { RECOM_ERROR_CODE, RECOM_REQUEST_TIMEOUT_MS } from '@/data/recom/constants';
import { createRecomApiError, isRecomApiError, toRecomApiError } from '@/data/recom/errors';
import type { RecomMethod } from '@/data/recom/endpoints';
import type { RecomEnvelope } from '@/data/recom/types';

export type RecomQuery = Record<string, string | number | boolean | undefined>;

export type RecomRequestOptions = {
	baseUrl: string;
	path: string;
	method: RecomMethod;
	body?: unknown;
	query?: RecomQuery;
	signal?: AbortSignal;
	timeoutMs?: number;
	headers?: Record<string, string>;
};

const withQuery = (url: string, query: RecomQuery | undefined) => {
	if (!query) return url;

	const params = new URLSearchParams();
	Object.entries(query).forEach(([name, value]) => {
		if (value !== undefined) params.set(name, String(value));
	});

	const search = params.toString();
	return search ? `${url}?${search}` : url;
};

const linkAbort = (signal: AbortSignal | undefined, timeoutMs: number) => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeoutMs);
	const forward = () => controller.abort();

	signal?.addEventListener('abort', forward);

	return {
		signal: controller.signal,
		release: () => {
			clearTimeout(timer);
			signal?.removeEventListener('abort', forward);
		},
	};
};

const readBody = async (response: Response): Promise<unknown> => {
	const text = await response.text();
	if (!text) return undefined;
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
};

const isEnvelope = (payload: unknown): payload is RecomEnvelope<unknown, unknown> =>
	typeof payload === 'object' && payload !== null && 'data' in payload;

const asRecomApiError = (error: unknown) => {
	if (isRecomApiError(error)) return error;
	const aborted = (error as Error)?.name === 'AbortError';
	return createRecomApiError({
		status: 0,
		code: RECOM_ERROR_CODE.proxy,
		detail: aborted ? 'La petición al recomendador fue cancelada o expiró' : String(error),
		payload: error,
	});
};

export const recomRequest = async <TData, TMeta = Record<string, never>>({
	baseUrl,
	path,
	method,
	body,
	query,
	signal,
	timeoutMs = RECOM_REQUEST_TIMEOUT_MS,
	headers,
}: RecomRequestOptions): Promise<RecomEnvelope<TData, TMeta>> => {
	const abort = linkAbort(signal, timeoutMs);

	try {
		const response = await fetch(withQuery(`${baseUrl}/${path}`, query), {
			method,
			headers: { 'Content-Type': 'application/json', accept: 'application/json', ...headers },
			...(method === 'GET' ? {} : { body: JSON.stringify(body) }),
			signal: abort.signal,
		});

		const payload = await readBody(response);

		if (!response.ok) throw toRecomApiError(response.status, payload);

		if (!isEnvelope(payload)) {
			throw createRecomApiError({
				status: response.status,
				code: RECOM_ERROR_CODE.invalidResponse,
				detail: 'La respuesta del recomendador no trae el envelope { data, meta }',
				payload,
			});
		}

		return payload as RecomEnvelope<TData, TMeta>;
	} catch (error) {
		throw asRecomApiError(error);
	} finally {
		abort.release();
	}
};
